import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { Category, Topic } from "@/types/content";
import TopicCard from "@/components/feature/TopicCard";

interface LatestFeedProps {
  topics: Topic[];
  categories: Category[];
}

export default function LatestFeed({ topics, categories }: LatestFeedProps) {
  const [active, setActive] = useState("all");

  const tabs = useMemo(
    () => [{ slug: "all", name: "全部" }, ...categories.map((c) => ({ slug: c.slug, name: c.name }))],
    [categories],
  );

  const filtered = useMemo(() => {
    if (active === "all") return topics;
    return topics.filter((t) => t.categorySlug === active);
  }, [active, topics]);

  return (
    <section id="latest" className="w-full border-b border-background-200 bg-background-100">
      <div className="mx-auto w-full max-w-[1240px] px-4 py-12 md:px-6 md:py-16">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="font-label text-xs tracking-[0.28em] text-secondary-600">
              LATEST FEED
            </span>
            <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              最新爆料流
            </h2>
            <p className="mt-2 max-w-2xl text-sm text-foreground-600">
              全天滚动补充新话题，按发布时间排列。点击分类可快速筛选你关心的领域。
            </p>
          </div>
          <Link
            to="/rank"
            className="whitespace-nowrap rounded-md bg-secondary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-secondary-600"
          >
            进入榜单页
          </Link>
        </div>

        <div
          className="mt-6 flex w-fit max-w-full flex-wrap items-center gap-1 rounded-full border border-background-300 bg-background-50 p-1"
          role="tablist"
          aria-label="按分类筛选爆料"
        >
          {tabs.map((tab) => (
            <button
              key={tab.slug}
              type="button"
              role="tab"
              aria-selected={active === tab.slug}
              onClick={() => setActive(tab.slug)}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                active === tab.slug
                  ? "bg-primary-500 text-background-50"
                  : "text-foreground-700 hover:bg-background-100"
              }`}
            >
              {tab.name}
            </button>
          ))}
        </div>

        {filtered.length > 0 ? (
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.slice(0, 6).map((topic) => (
              <TopicCard key={topic.id} topic={topic} />
            ))}
          </div>
        ) : (
          <p className="mt-10 rounded-lg border border-dashed border-background-300 bg-background-50 p-8 text-center text-sm text-foreground-500">
            该分类暂无新的爆料，稍后再来看看。
          </p>
        )}

        <div className="mt-8 flex justify-center">
          <Link
            to="/rank"
            className="whitespace-nowrap rounded-md border border-background-300 px-5 py-3 text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
          >
            浏览全部爆料 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}